/**
 * Cleanup Legacy localStorage Data
 *
 * This removes old trip data from localStorage that's no longer needed
 * after migration to IndexedDB-based trips system.
 */

const KEYS_TO_PRESERVE = [
  'auth_token',
  'auth_system_initialized',
  'onboardingCompleted',
  'trips_index', // New trips system
  'legacy_trip_migrated_v1', // Migration flag
  'premium_license',
  'budgetData',
  'install-prompt-dismissed',
  'update-prompt-dismissed'
];

/**
 * Check if a key should be preserved
 */
function shouldPreserve(key) {
  // Preserve specific keys
  if (KEYS_TO_PRESERVE.includes(key)) return true;

  // Preserve user-specific keys
  if (key.startsWith('user_data_')) return true;

  // Preserve current user settings (we'll update it from active trip)
  if (key === 'userSettings') return false; // Will be regenerated from active trip

  return false;
}

/**
 * Clean up old localStorage trip data
 */
export function cleanupLegacyTripData() {
  console.log('[Cleanup] Starting legacy data cleanup...');

  const keysToRemove = [];

  // Find all keys that should be removed
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && !shouldPreserve(key)) {
      keysToRemove.push(key);
    }
  }

  console.log('[Cleanup] Keys to remove:', keysToRemove);

  // Remove old trip data
  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
      console.log(`[Cleanup] Removed: ${key}`);
    } catch (error) {
      console.error(`[Cleanup] Failed to remove ${key}:`, error);
    }
  });

  console.log(`[Cleanup] ✅ Cleaned up ${keysToRemove.length} old keys`);

  return {
    removedCount: keysToRemove.length,
    removedKeys: keysToRemove
  };
}

/**
 * Update userSettings from active trip
 */
export async function syncUserSettingsFromActiveTrip() {
  try {
    const storageManager = (await import('./storageManager.js')).default;
    await storageManager.initialize();

    const activeTrip = await storageManager.getActiveTrip();

    if (activeTrip) {
      const currentSettings = JSON.parse(localStorage.getItem('userSettings') || '{}');

      const updatedSettings = {
        ...currentSettings,
        destination: activeTrip.destination?.city || currentSettings.destination || 'CITY',
        location: activeTrip.destination?.city || currentSettings.location || 'CITY',
        country: activeTrip.destination?.country || currentSettings.country || 'COUNTRY'
      };

      localStorage.setItem('userSettings', JSON.stringify(updatedSettings));

      console.log('[Cleanup] ✅ Synced userSettings from active trip:', updatedSettings);

      return updatedSettings;
    } else {
      console.log('[Cleanup] No active trip to sync');
      return null;
    }
  } catch (error) {
    console.error('[Cleanup] Failed to sync userSettings:', error);
    return null;
  }
}

export default cleanupLegacyTripData;
